import React, { useEffect, useState } from "react";

import {
    Shield,
    Globe,
    Server,
    Printer,
    AlertTriangle,
    CheckCircle2,
    Search,
    Network
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";


interface ProbePortsProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

interface PortData {
    id: string;
    port: number;
    product: string;
    protocol: string; // "tcp" | "udp"
    service: string;
    status: string; // "open"
    updated: string;
}

const ProbePorts: React.FC<ProbePortsProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [portsData, setPortsData] = useState<PortData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id,
                })
            };
            // NOTE: Ensure your backend endpoint returns this data structure. 
            // If this is a different endpoint (e.g. /openports), update the URL below.
            const response = await baseapi.get('/assetexternalports', { params });

            if (response.data.status) {
                // Sort ports numerically
                const sortedData = (response.data.data || []).sort((a: PortData, b: PortData) => a.port - b.port);
                setPortsData(sortedData);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    // --- Helpers ---

    const getServiceIcon = (service: string) => {
        const s = service.toLowerCase();
        if (s.includes('http') || s.includes('www')) return <Globe className="w-4 h-4 text-blue-400" />;
        if (s.includes('printer') || s.includes('jetdirect') || s.includes('ipp')) return <Printer className="w-4 h-4 text-orange-400" />;
        if (s.includes('ssh') || s.includes('telnet') || s.includes('ftp')) return <Server className="w-4 h-4 text-purple-400" />;
        return <Network className="w-4 h-4 text-gray-400" />;
    };

    const isUnsecure = (port: number, service: string) => {
        // Simple logic to flag potentially unencrypted services
        return (port === 80 || port === 21 || port === 23 || service === 'http' || service === 'telnet' || service === 'ftp');
    };

    // Filter logic
    const filteredPorts = portsData.filter(p =>
        p.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.port.toString().includes(searchTerm) ||
        p.product.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Stats
    const totalPorts = portsData.length;
    const webPorts = portsData.filter(p => p.service.includes('http')).length;
    const printerPorts = portsData.filter(p => ['ipp', 'printer', 'jetdirect'].some(s => p.service.includes(s))).length;

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center text-gray-400 animate-pulse">
                Loading port scan results...
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">

            {/* Top Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs uppercase font-semibold">Open Ports</p>
                        <p className="text-2xl font-bold text-white mt-1">{totalPorts}</p>
                    </div>
                    <div className="p-3 bg-blue-900/20 rounded-full border border-blue-500/20">
                        <Shield className="w-6 h-6 text-blue-400" />
                    </div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs uppercase font-semibold">Web Services</p>
                        <p className="text-2xl font-bold text-white mt-1">{webPorts}</p>
                    </div>
                    <div className="p-3 bg-purple-900/20 rounded-full border border-purple-500/20">
                        <Globe className="w-6 h-6 text-purple-400" />
                    </div>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-xs uppercase font-semibold">Printer Services</p>
                        <p className="text-2xl font-bold text-white mt-1">{printerPorts}</p>
                    </div>
                    <div className="p-3 bg-orange-900/20 rounded-full border border-orange-500/20">
                        <Printer className="w-6 h-6 text-orange-400" />
                    </div>
                </div>
            </div>

            {/* Main Table Section */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden flex flex-col">

                {/* Header & Search */}
                <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Network className="w-5 h-5 text-orange-500" />
                        Port Scan Results
                    </h3>

                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search ports, services..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-800 text-gray-400 uppercase text-xs font-medium">
                            <tr>
                                <th className="px-6 py-3">Port / Protocol</th>
                                <th className="px-6 py-3">Service</th>
                                <th className="px-6 py-3">Product / Version</th>
                                <th className="px-6 py-3 text-center">Status</th>
                                <th className="px-6 py-3 text-right">Security Check</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {filteredPorts.length > 0 ? (
                                filteredPorts.map((item) => {
                                    const insecure = isUnsecure(item.port, item.service);
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-800/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-mono text-lg font-semibold text-white bg-gray-800 px-2 py-1 rounded border border-gray-700">
                                                        {item.port}
                                                    </span>
                                                    <span className="text-xs text-gray-500 font-mono uppercase">
                                                        {item.protocol}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {getServiceIcon(item.service)}
                                                    <span className="text-gray-200 font-medium capitalize">{item.service}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-400">
                                                {item.product ? (
                                                    <span className="text-gray-300">{item.product}</span>
                                                ) : (
                                                    <span className="text-gray-600 italic text-xs">Unknown Product</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.status === 'open'
                                                    ? 'bg-green-900/20 text-green-400 border-green-900/30'
                                                    : 'bg-gray-800 text-gray-400 border-gray-700'
                                                    }`}>
                                                    <div className="w-1.5 h-1.5 rounded-full bg-current" />
                                                    {item.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {insecure ? (
                                                    <div className="flex items-center justify-end gap-1 text-amber-500 text-xs" title="Unencrypted service detected">
                                                        <AlertTriangle className="w-4 h-4" />
                                                        <span>Unencrypted</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-end gap-1 text-gray-500 text-xs">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        <span>Standard</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                        <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
                                        <p>No ports found matching your search.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProbePorts;
